'use client';

import React, { useState, useRef, useEffect } from 'react';

interface DropdownButtonProps {
  icon: React.ReactNode;
  text: string;
  options: { value: string; label: string; icon?: React.ReactNode }[];
  selected: string;
  onSelect: (value: string) => void;
}

/**
 * Dropdown button component with an icon, text and arrow
 */
const DropdownButton: React.FC<DropdownButtonProps> = ({
  icon,
  text,
  options,
  selected,
  onSelect
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative w-full" ref={ref}>
      <button
        className="flex items-center w-full px-4 py-3 rounded-4xl bg-white shadow-md hover:bg-gray-50 transition justify-between"
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 flex items-center justify-center">{icon}</span>
          <span className="font-semibold text-gray-700 text-lg">{text}</span>
        </div>
        <svg className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`} width="18" height="18" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L5 5L9 1" stroke="#656565" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-full bg-white rounded-2xl shadow-xl z-50 p-4 flex flex-col gap-6 animate-fade-in">
          {options.map(opt => (
            <button
              key={opt.value}
              className="flex items-center justify-between w-full px-2 py-3 rounded-xl hover:bg-gray-100 transition"
              onClick={() => { onSelect(opt.value); setOpen(false); }}
              type="button"
            >
              <div className="flex items-center gap-4">
                {opt.icon && <span className="w-10 h-10 flex items-center justify-center">{opt.icon}</span>}
                <span className="font-bold text-gray-700 text-xl drop-shadow-sm">{opt.label}</span>
              </div>
              {selected === opt.value ? (
                <span className="w-8 h-8 flex items-center justify-center bg-green-400 rounded-full shadow-lg">
                  <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 9L8 12L13 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              ) : (
                <span className="w-8 h-8 flex items-center justify-center border-4 border-gray-200 rounded-full shadow-lg bg-white"></span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownButton;
